import { useEffect, useState } from 'react';
import { Button, Card, Col, Collapse, Container, ListGroup, Row } from 'react-bootstrap';
import { fetchFeedback } from '../../api/api';
import { Feedback as FeedbackEnum } from '../../api/models';
import './Feedback.css';

interface Feedback {
    type: string;
    user_name: string;
    content: string;
    created_at: string;
    detailed_feedback?: string;
    user_question?: string;
}

interface FeedbacksResponse {
    feedback: Feedback[];
    messages_without_feedback_count: number;
    positive_feedback_count: number;
}

const Feedbacks = () => {
    const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [openContentIndex, setOpenContentIndex] = useState<number | null>(null);
    const [positiveFeedbackCount, setPositiveFeedbackCount] = useState<number>(0);
    const [messagesWithoutFeedbackCount, setMessagesWithoutFeedbackCount] = useState<number>(0);

    useEffect(() => {
        const getFeedbacks = async () => {
            try {
                const response: FeedbacksResponse = await fetchFeedback();
                setFeedbacks(response.feedback);
                setPositiveFeedbackCount(response.positive_feedback_count);
                setMessagesWithoutFeedbackCount(response.messages_without_feedback_count);
            } catch (err: any) {
                setError(err.message || 'An error occurred');
            } finally {
                setLoading(false);
            }
        };

        getFeedbacks();
    }, []);

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;
    if (feedbacks.length === 0) return <div>No feedback available</div>;

    return (
        <Container>
            <h1 className="text-center my-4">User Feedback</h1>
            <Row className="mb-4">
                <Col md={3}><Card className="text-center p-3"><h4>Total Feedbacks</h4><h2>{feedbacks.length + positiveFeedbackCount}</h2></Card></Col>
                <Col md={3}><Card className="text-center p-3"><h4>Positive</h4><h2>{positiveFeedbackCount}</h2></Card></Col>
                <Col md={3}><Card className="text-center p-3"><h4>Negative</h4><h2>{feedbacks.length}</h2></Card></Col>
                <Col md={3}><Card className="text-center p-3"><h4>Without Feedback</h4><h2>{messagesWithoutFeedbackCount}</h2></Card></Col>
            </Row>
            <Row>
                {feedbacks.map((feedback, index) => (
                    <Col md={6} key={index} className="mb-3">
                        <Card className={`feedback-card ${feedback.type}`}>
                            <Card.Body>
                                <Card.Title className="feedback-title">Feedback Type: {Object.keys(FeedbackEnum).find(
                                    key => FeedbackEnum[key as keyof typeof FeedbackEnum] === feedback.type
                                ) || feedback.type}</Card.Title>
                                <ListGroup>
                                    <ListGroup.Item>
                                        <strong>User:</strong> {feedback.user_name}<br />
                                        <strong>Date:</strong> {new Date(feedback.created_at).toLocaleString()}<br />
                                        {feedback.user_question && (
                                            <div>
                                                <strong>User Question:</strong>
                                                <p>{feedback.user_question}</p>
                                            </div>
                                        )}
                                        {feedback.detailed_feedback && (
                                            <div>
                                                <strong>Detailed Feedback:</strong>
                                                <p>{feedback.detailed_feedback}</p>
                                            </div>
                                        )}
                                        <Button variant="link" onClick={() => setOpenContentIndex(openContentIndex === index ? null : index)}>
                                            {openContentIndex === index ? 'Hide Content' : 'Show Content'}
                                        </Button>
                                        <Collapse in={openContentIndex === index}>
                                            <div>
                                                <strong>Content:</strong>
                                                <div dangerouslySetInnerHTML={{ __html: feedback.content }} />
                                            </div>
                                        </Collapse>
                                    </ListGroup.Item>
                                </ListGroup>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>
        </Container>
    );
};

export default Feedbacks;